import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { codingQuestionsAPI } from '../../services/api'

// Data types supported
const DATA_TYPES = [
  { value: 'integer', label: 'Integer' },
  { value: 'float', label: 'Float' },
  { value: 'string', label: 'String' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'list_int', label: 'List of Integers' },
  { value: 'list_string', label: 'List of Strings' },
  { value: 'array', label: 'Generic Array' },
  { value: 'array_2d', label: '2D Array' },
  { value: 'object', label: 'JSON Object' },
  { value: 'tree_node', label: 'Tree Node' },
  { value: 'linked_list', label: 'Linked List' }
]

const PROGRAMMING_LANGUAGES = [
  { value: 'python', label: 'Python' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'java', label: 'Java' },
  { value: 'cpp', label: 'C++' },
  { value: 'c', label: 'C' },
  { value: 'go', label: 'Go' }
]

// Judge0 Harness input/output formats
const INPUT_FORMATS = [
  { value: 'array', label: 'Array' },
  { value: 'string', label: 'String' },
  { value: 'integer', label: 'Integer' },
  { value: 'array_and_target', label: 'Array and Target' },
  { value: 'matrix', label: 'Matrix' },
  { value: 'json', label: 'JSON Object' },
  // Combined input formats for multiple parameters
  { value: 'string_and_integer', label: 'String + Integer' },
  { value: 'array_and_string', label: 'Array + String' },
  { value: 'array_and_integer', label: 'Array + Integer' },
  { value: 'string_and_array', label: 'String + Array' },
  { value: 'integer_and_string', label: 'Integer + String' },
  { value: 'multiple_parameters', label: 'Multiple Parameters (Custom)' },
  { value: 'custom_input', label: 'Custom Input Format' }
]

const OUTPUT_FORMATS = [
  { value: 'array', label: 'Array' },
  { value: 'string', label: 'String' },
  { value: 'integer', label: 'Integer' },
  { value: 'json', label: 'JSON Object' },
  // Combined output formats for complex return types
  { value: 'array_of_strings', label: 'Array of Strings' },
  { value: 'array_of_integers', label: 'Array of Integers' },
  { value: 'array_of_objects', label: 'Array of Objects' },
  { value: 'tuple', label: 'Tuple (Multiple Values)' },
  { value: 'object_with_array', label: 'Object with Array' },
  { value: 'object_with_string', label: 'Object with String' },
  { value: 'nested_object', label: 'Nested Object' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'float', label: 'Float/Double' },
  { value: 'complex_output', label: 'Complex Output (Custom)' },
  { value: 'custom_output', label: 'Custom Output Format' }
]

interface TestCaseInput {
  name: string
  value: any
  data_type: string
}

interface TestCaseOutput {
  value: any
  data_type: string
}

interface ExampleTestCase {
  id: number
  inputs: TestCaseInput[]
  output: TestCaseOutput
  explanation: string
}

interface HiddenTestCase {
  id: number
  inputs: TestCaseInput[]
  output: TestCaseOutput
  weight: number
}

interface FunctionParameter {
  name: string
  type: string
}

interface StarterCodeItem {
  language: string
  code: string
}

interface Judge0HarnessConfig {
  input_format: 'array' | 'string' | 'integer' | 'array_and_target' | 'matrix' | 'json' | 
                'string_and_integer' | 'array_and_string' | 'array_and_integer' | 
                'string_and_array' | 'integer_and_string' | 'multiple_parameters' | 'custom_input'
  output_format: 'array' | 'string' | 'integer' | 'json' | 'array_of_strings' | 'array_of_integers' |
                 'array_of_objects' | 'tuple' | 'object_with_array' | 'object_with_string' |
                 'nested_object' | 'boolean' | 'float' | 'complex_output' | 'custom_output'
  function_name: string
  templates: {
    python3: string
    cpp17: string
    java: string
    node: string
    javascript: string
  }
  // Custom format specifications
  custom_input_format?: string
  custom_output_format?: string
}

const CreateCodingQuestionPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { category } = location.state || {}
  
  const [loading, setLoading] = useState(false)
  
  // Main question data
  const [title, setTitle] = useState('')
  const [problemStatement, setProblemStatement] = useState('')
  const [difficultyLevel, setDifficultyLevel] = useState<'easy' | 'medium' | 'hard'>('easy')
  
  // Function signature
  const [functionName, setFunctionName] = useState('')
  const [parameters, setParameters] = useState<FunctionParameter[]>([{ name: '', type: '' }])
  const [returnType, setReturnType] = useState('')
  
  // Test cases
  const [exampleTestCases, setExampleTestCases] = useState<ExampleTestCase[]>([
    {
      id: 1,
      inputs: [{ name: '', value: '', data_type: 'integer' }],
      output: { value: '', data_type: 'integer' },
      explanation: ''
    }
  ])
  
  const [hiddenTestCases, setHiddenTestCases] = useState<HiddenTestCase[]>([
    {
      id: 1,
      inputs: [{ name: '', value: '', data_type: 'integer' }],
      output: { value: '', data_type: 'integer' },
      weight: 1.0
    }
  ])
  
  // Constraints
  const [constraints, setConstraints] = useState<string[]>([''])
  
  // Starter code
  const [starterCode, setStarterCode] = useState<StarterCodeItem[]>([
    { language: 'python', code: '' }
  ])
  
  // Language restrictions
  const [allowedLanguages, setAllowedLanguages] = useState<string[]>(['python', 'javascript', 'java'])
  
  // Complexities
  const [timeComplexity, setTimeComplexity] = useState('')
  const [spaceComplexity, setSpaceComplexity] = useState('')
  
  // Judge0 Harness configuration
  const [judge0Config, setJudge0Config] = useState<Judge0HarnessConfig>({
    input_format: 'array',
    output_format: 'integer',
    function_name: '',
    templates: {
      python3: 'python_generic.j2',
      cpp17: 'cpp_generic.j2',
      java: 'java_generic.j2',
      node: 'js_generic.j2',
      javascript: 'js_generic.j2'
    },
    custom_input_format: '',
    custom_output_format: ''
  })
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    functionSignature: true,
    exampleTests: true,
    hiddenTests: true,
    constraints: false,
    starterCode: false,
    judge0Harness: false,
    advanced: false
  })
  
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }
  
  // Helper functions for example test cases
  const addExampleTestCase = () => {
    const newId = Math.max(...exampleTestCases.map(tc => tc.id), 0) + 1
    setExampleTestCases([
      ...exampleTestCases,
      {
        id: newId,
        inputs: [{ name: '', value: '', data_type: 'integer' }],
        output: { value: '', data_type: 'integer' },
        explanation: ''
      }
    ])
  }
  
  const removeExampleTestCase = (id: number) => {
    if (exampleTestCases.length > 1) {
      setExampleTestCases(exampleTestCases.filter(tc => tc.id !== id))
    }
  }
  
  const updateExampleTestCase = (id: number, field: keyof ExampleTestCase, value: any) => {
    setExampleTestCases(exampleTestCases.map(tc =>
      tc.id === id ? { ...tc, [field]: value } : tc
    ))
  }
  
  const addExampleInput = (testCaseId: number) => {
    setExampleTestCases(exampleTestCases.map(tc =>
      tc.id === testCaseId
        ? { ...tc, inputs: [...tc.inputs, { name: '', value: '', data_type: 'integer' }] }
        : tc
    ))
  }
  
  const removeExampleInput = (testCaseId: number, inputIndex: number) => {
    setExampleTestCases(exampleTestCases.map(tc =>
      tc.id === testCaseId
        ? { ...tc, inputs: tc.inputs.filter((_, i) => i !== inputIndex) }
        : tc
    ))
  }
  
  const updateExampleInput = (testCaseId: number, inputIndex: number, field: keyof TestCaseInput, value: any) => {
    setExampleTestCases(exampleTestCases.map(tc =>
      tc.id === testCaseId
        ? {
            ...tc,
            inputs: tc.inputs.map((input, i) =>
              i === inputIndex ? { ...input, [field]: value } : input
            )
          }
        : tc
    ))
  }
  
  // Helper functions for hidden test cases
  const addHiddenTestCase = () => {
    const newId = Math.max(...hiddenTestCases.map(tc => tc.id), 0) + 1
    setHiddenTestCases([
      ...hiddenTestCases,
      {
        id: newId,
        inputs: [{ name: '', value: '', data_type: 'integer' }],
        output: { value: '', data_type: 'integer' },
        weight: 1.0
      }
    ])
  }
  
  const removeHiddenTestCase = (id: number) => {
    if (hiddenTestCases.length > 1) {
      setHiddenTestCases(hiddenTestCases.filter(tc => tc.id !== id))
    }
  }
  
  const updateHiddenTestCase = (id: number, field: keyof HiddenTestCase, value: any) => {
    setHiddenTestCases(hiddenTestCases.map(tc =>
      tc.id === id ? { ...tc, [field]: value } : tc
    ))
  }
  
  const addHiddenInput = (testCaseId: number) => {
    setHiddenTestCases(hiddenTestCases.map(tc =>
      tc.id === testCaseId
        ? { ...tc, inputs: [...tc.inputs, { name: '', value: '', data_type: 'integer' }] }
        : tc
    ))
  }
  
  const removeHiddenInput = (testCaseId: number, inputIndex: number) => {
    setHiddenTestCases(hiddenTestCases.map(tc =>
      tc.id === testCaseId
        ? { ...tc, inputs: tc.inputs.filter((_, i) => i !== inputIndex) }
        : tc
    ))
  }
  
  const updateHiddenInput = (testCaseId: number, inputIndex: number, field: keyof TestCaseInput, value: any) => {
    setHiddenTestCases(hiddenTestCases.map(tc =>
      tc.id === testCaseId
        ? {
            ...tc,
            inputs: tc.inputs.map((input, i) =>
              i === inputIndex ? { ...input, [field]: value } : input
            )
          }
        : tc
    ))
  }
  
  // Function parameter helpers
  const addParameter = () => {
    setParameters([...parameters, { name: '', type: '' }])
  }
  
  const removeParameter = (index: number) => {
    if (parameters.length > 1) {
      setParameters(parameters.filter((_, i) => i !== index))
    }
  }
  
  const updateParameter = (index: number, field: 'name' | 'type', value: string) => {
    // Validation: Check for multiple parameters in one field
    if (field === 'name' && value.includes(',')) {
      toast.error('❌ Please enter only ONE parameter name per field. Use "Add Parameter" to create multiple parameters.')
      return
    }
    
    if (field === 'type' && value.includes(',')) {
      toast.error('❌ Please enter only ONE parameter type per field. Use "Add Parameter" to create multiple parameters.')
      return
    }
    
    setParameters(parameters.map((param, i) => {
      if (i === index) {
        return { ...param, [field]: value }
      }
      return param
    }))
  }
  
  // Constraint helpers
  const addConstraint = () => {
    setConstraints([...constraints, ''])
  }
  
  const removeConstraint = (index: number) => {
    setConstraints(constraints.filter((_, i) => i !== index))
  }
  
  const updateConstraint = (index: number, value: string) => {
    setConstraints(constraints.map((c, i) => i === index ? value : c))
  }
  
  // Starter code helpers
  const addStarterCode = () => {
    setStarterCode([...starterCode, { language: 'python', code: '' }])
  }
  
  const removeStarterCode = (index: number) => {
    setStarterCode(starterCode.filter((_, i) => i !== index))
  }
  
  const updateStarterCode = (index: number, field: 'language' | 'code', value: string) => {
    setStarterCode(starterCode.map((sc, i) =>
      i === index ? { ...sc, [field]: value } : sc
    ))
  }
  
  // Parse input value based on data type
  const parseInputValue = (value: string, dataType: string): any => {
    if (!value.trim()) return null
    
    try {
      switch (dataType) {
        case 'integer':
          return parseInt(value, 10)
        case 'float':
          return parseFloat(value)
        case 'boolean':
          return value.toLowerCase() === 'true'
        case 'string':
          return value
        case 'list_int':
        case 'list_string':
        case 'array':
        case 'array_2d':
        case 'object':
          return JSON.parse(value)
        default:
          return value
      }
    } catch (error) {
      return value
    }
  }
  
  // Type-safe language mapping
  type SupportedLanguage = 'python' | 'java' | 'cpp' | 'c' | 'javascript' | 'go'
  type BaseType = 'int' | 'str' | 'bool' | 'float'
  
  // Utility function to validate language support
  const isValidLanguage = (lang: string): lang is SupportedLanguage => {
    return ['python', 'java', 'cpp', 'c', 'javascript', 'go'].includes(lang)
  }
  
  // Dynamic data type conversion for different languages
  const convertDataType = (type: string, language: SupportedLanguage): string => {
    // Trim whitespace from type string
    const trimmedType = type.trim()
    
    // Base type mappings for common types - type-safe
    const baseTypes: Record<BaseType, Record<SupportedLanguage, string>> = {
      'int': { 'python': 'int', 'java': 'int', 'cpp': 'int', 'c': 'int', 'javascript': 'number', 'go': 'int' },
      'str': { 'python': 'str', 'java': 'String', 'cpp': 'string', 'c': 'char*', 'javascript': 'string', 'go': 'string' },
      'bool': { 'python': 'bool', 'java': 'boolean', 'cpp': 'bool', 'c': 'int', 'javascript': 'boolean', 'go': 'bool' },
      'float': { 'python': 'float', 'java': 'double', 'cpp': 'double', 'c': 'double', 'javascript': 'number', 'go': 'float64' }
    }
    
    // Collection type patterns with type-safe converters
    type TypeConverter = (inner: string) => string
    type ConverterMap = Record<SupportedLanguage, TypeConverter>
    
    const collectionPatterns: Array<{
      pattern: RegExp
      converters: ConverterMap
    }> = [
      { 
        pattern: /^List\[(.+)\]$/, 
        converters: {
          'python': (inner: string) => `List[${inner}]`,
          'java': (inner: string) => `List<${inner === 'int' ? 'Integer' : inner === 'str' ? 'String' : inner}>`,
          'cpp': (inner: string) => `vector<${inner}>`,
          'c': (inner: string) => inner === 'int' ? 'int*' : inner === 'str' ? 'char**' : `${inner}*`,
          'javascript': (inner: string) => inner === 'int' ? 'number[]' : inner === 'str' ? 'string[]' : `${inner}[]`,
          'go': (inner: string) => `[]${inner}`
        }
      },
      { 
        pattern: /^Array\[(.+)\]$/, 
        converters: {
          'python': (inner: string) => `List[${inner}]`,
          'java': (inner: string) => `${inner}[]`,
          'cpp': (inner: string) => `${inner}[]`,
          'c': (inner: string) => `${inner}*`,
          'javascript': (inner: string) => `${inner}[]`,
          'go': (inner: string) => `[]${inner}`
        }
      },
      { 
        pattern: /^Set\[(.+)\]$/, 
        converters: {
          'python': (inner: string) => `Set[${inner}]`,
          'java': (inner: string) => `Set<${inner === 'int' ? 'Integer' : inner === 'str' ? 'String' : inner}>`,
          'cpp': (inner: string) => `set<${inner}>`,
          'c': (inner: string) => `${inner}*`,
          'javascript': (inner: string) => `Set<${inner}>`,
          'go': (inner: string) => `map[${inner}]bool`
        }
      }
    ]
    
    // Check for collection types first
    for (const { pattern, converters } of collectionPatterns) {
      const match = trimmedType.match(pattern)
      if (match) {
        const innerType = match[1]
        const convertedInnerType = convertDataType(innerType, language) // Recursive conversion
        return converters[language]?.(convertedInnerType) || trimmedType
      }
    }
    
    // Check base types with type safety
    if (trimmedType in baseTypes && language in baseTypes[trimmedType as BaseType]) {
      return baseTypes[trimmedType as BaseType][language]
    }
    
    // For unknown types, return as-is (allows for custom types)
    return trimmedType
  }

  // Generate starter code with function signature
  const generateStarterCode = (language: SupportedLanguage): string => {
    if (!functionName.trim()) {
      return ''
    }
    
    if (!returnType.trim()) {
      return ''
    }
    
    const validParams = parameters.filter(p => p.name.trim() && p.type.trim())
    
    if (validParams.length === 0) {
      return ''
    }
    
    switch (language.toLowerCase()) {
      case 'python':
        const pythonParams = validParams.map(p => `${p.name}: ${convertDataType(p.type, 'python')}`).join(', ')
        const pythonReturnType = convertDataType(returnType, 'python')
        return `def ${functionName}(${pythonParams}) -> ${pythonReturnType}:\n    # Write your solution here\n    pass\n`
      
      case 'java':
        const javaParams = validParams.map(p => `${convertDataType(p.type, 'java')} ${p.name}`).join(', ')
        const javaReturnType = convertDataType(returnType, 'java')
        return `public class Solution {\n    public static ${javaReturnType} ${functionName}(${javaParams}) {\n        // Write your solution here\n        return null;\n    }\n}\n`
      
      case 'cpp':
        const cppParams = validParams.map(p => `${convertDataType(p.type, 'cpp')} ${p.name}`).join(', ')
        const cppReturnType = convertDataType(returnType, 'cpp')
        return `${cppReturnType} ${functionName}(${cppParams}) {\n    // Write your solution here\n    return {};\n}\n`
      
      case 'c':
        const cParams = validParams.map(p => `${convertDataType(p.type, 'c')} ${p.name}`).join(', ')
        const cReturnType = convertDataType(returnType, 'c')
        return `${cReturnType} ${functionName}(${cParams}) {\n    // Write your solution here\n    return NULL;\n}\n`
      
      case 'javascript':
        const jsParams = validParams.map(p => p.name).join(', ')
        return `function ${functionName}(${jsParams}) {\n    // Write your solution here\n    \n}\n`
      
      case 'typescript':
        const tsParams = validParams.map(p => `${p.name}: ${convertDataType(p.type, 'javascript')}`).join(', ')
        const tsReturnType = convertDataType(returnType, 'javascript')
        return `function ${functionName}(${tsParams}): ${tsReturnType} {\n    // Write your solution here\n    \n}\n`
      
      case 'go':
        const goParams = validParams.map(p => `${p.name} ${convertDataType(p.type, 'go')}`).join(', ')
        const goReturnType = convertDataType(returnType, 'go')
        return `func ${functionName}(${goParams}) ${goReturnType} {\n    // Write your solution here\n    \n}\n`
      
      default:
        return `// ${functionName} function\n// Parameters: ${validParams.map(p => `${p.name}: ${p.type}`).join(', ')}\n// Return type: ${returnType}\n\n`
    }
  }
  
  // Auto-generate starter code when function signature changes
  const updateStarterCodeForLanguage = (language: SupportedLanguage) => {
    const generatedCode = generateStarterCode(language)
    
    if (generatedCode) {
      setStarterCode(prev => {
        const existing = prev.find(sc => sc.language === language)
        if (existing) {
          return prev.map(sc => 
            sc.language === language 
              ? { ...sc, code: generatedCode }
              : sc
          )
        } else {
          return [...prev, { language, code: generatedCode }]
        }
      })
    }
  }
  
  // Auto-generate Judge0 Harness configuration based on function signature
  const generateJudge0Config = () => {
    if (!functionName || parameters.length === 0 || !returnType) {
      return
    }
    
    // Determine input format based on parameters
    let inputFormat: Judge0HarnessConfig['input_format'] = 'array'
    
    if (parameters.length === 1) {
      const paramType = parameters[0].type.toLowerCase()
      if (paramType.includes('string')) {
        inputFormat = 'string'
      } else if (paramType.includes('int') && !paramType.includes('list') && !paramType.includes('array')) {
        inputFormat = 'integer'
      } else if (paramType.includes('list') || paramType.includes('array')) {
        inputFormat = 'array'
      } else if (paramType.includes('matrix') || paramType.includes('2d')) {
        inputFormat = 'matrix'
      } else if (paramType.includes('object') || paramType.includes('json')) {
        inputFormat = 'json'
      }
    } else if (parameters.length === 2) {
      // Enhanced logic for two parameters
      const [firstParam, secondParam] = parameters
      const firstType = firstParam.type.toLowerCase()
      const secondType = secondParam.type.toLowerCase()
      
      // Check for specific two-parameter combinations
      if (firstType.includes('string') && secondType.includes('int') && !secondType.includes('list') && !secondType.includes('array')) {
        inputFormat = 'string_and_integer'
      } else if (firstType.includes('list') || firstType.includes('array')) {
        if (secondType.includes('string')) {
          inputFormat = 'array_and_string'
        } else if (secondType.includes('int') && !secondType.includes('list') && !secondType.includes('array')) {
          inputFormat = 'array_and_integer'
        } else {
          inputFormat = 'array_and_target'
        }
      } else if (firstType.includes('string') && (secondType.includes('list') || secondType.includes('array'))) {
        inputFormat = 'string_and_array'
      } else if (firstType.includes('int') && !firstType.includes('list') && !firstType.includes('array') && secondType.includes('string')) {
        inputFormat = 'integer_and_string'
      } else {
        inputFormat = 'array_and_target'
      }
    } else if (parameters.length > 2) {
      // For more than 2 parameters, use custom format
      inputFormat = 'multiple_parameters'
    }
    
    // Determine output format based on return type
    let outputFormat: Judge0HarnessConfig['output_format'] = 'integer'
    const returnTypeLower = returnType.toLowerCase()
    
    // Enhanced output format detection
    if (returnTypeLower.includes('list') || returnTypeLower.includes('array')) {
      // Check for specific array types
      if (returnTypeLower.includes('string')) {
        outputFormat = 'array_of_strings'
      } else if (returnTypeLower.includes('int') || returnTypeLower.includes('integer')) {
        outputFormat = 'array_of_integers'
      } else if (returnTypeLower.includes('object') || returnTypeLower.includes('dict')) {
        outputFormat = 'array_of_objects'
      } else {
        outputFormat = 'array'
      }
    } else if (returnTypeLower.includes('tuple')) {
      outputFormat = 'tuple'
    } else if (returnTypeLower.includes('object') || returnTypeLower.includes('json') || returnTypeLower.includes('dict')) {
      // Check for complex object types
      if (returnTypeLower.includes('array') || returnTypeLower.includes('list')) {
        outputFormat = 'object_with_array'
      } else if (returnTypeLower.includes('string')) {
        outputFormat = 'object_with_string'
      } else if (returnTypeLower.includes('nested') || returnTypeLower.includes('complex')) {
        outputFormat = 'nested_object'
      } else {
        outputFormat = 'json'
      }
    } else if (returnTypeLower.includes('string')) {
      outputFormat = 'string'
    } else if (returnTypeLower.includes('bool')) {
      outputFormat = 'boolean'
    } else if (returnTypeLower.includes('float') || returnTypeLower.includes('double')) {
      outputFormat = 'float'
    } else if (returnTypeLower.includes('int') || returnTypeLower.includes('integer')) {
      outputFormat = 'integer'
    } else if (returnTypeLower.includes('complex') || returnTypeLower.includes('custom')) {
      outputFormat = 'complex_output'
    }
    
    // Update Judge0 configuration
    setJudge0Config(prev => ({
      ...prev,
      input_format: inputFormat,
      output_format: outputFormat,
      function_name: functionName
    }))
  }
  
  // Auto-generate Judge0 config when function signature changes
  useEffect(() => {
    generateJudge0Config()
  }, [functionName, parameters, returnType])
  
  // Validation
  const validateForm = (): boolean => {
    if (!title.trim()) {
      toast.error('Question title is required')
      return false
    }
    
    if (!problemStatement.trim()) {
      toast.error('Problem statement is required')
      return false
    }
    
    if (!functionName.trim()) {
      toast.error('Function name is required')
      return false
    }
    
    if (parameters.some(p => !p.name.trim() || !p.type.trim())) {
      toast.error('All function parameters must have name and type')
      return false
    }
    
    if (!returnType.trim()) {
      toast.error('Return type is required')
      return false
    }
    
    // Validate custom formats
    if (judge0Config.input_format === 'custom_input' && !judge0Config.custom_input_format?.trim()) {
      toast.error('Custom input format specification is required when using custom input format')
      return false
    }
    
    if (judge0Config.output_format === 'custom_output' && !judge0Config.custom_output_format?.trim()) {
      toast.error('Custom output format specification is required when using custom output format')
      return false
    }
    
    
    // Validate example test cases
    for (const tc of exampleTestCases) {
      if (tc.inputs.some(input => !input.name.trim() || !input.value)) {
        toast.error('All example test case inputs must have name and value')
        return false
      }
      if (!tc.output.value) {
        toast.error('All example test cases must have an output value')
        return false
      }
    }
    
    // Validate hidden test cases
    for (const tc of hiddenTestCases) {
      if (tc.inputs.some(input => !input.name.trim() || !input.value)) {
        toast.error('All hidden test case inputs must have name and value')
        return false
      }
      if (!tc.output.value) {
        toast.error('All hidden test cases must have an output value')
        return false
      }
    }
    
    return true
  }
  
  // Submit handler
  const handleSubmit = async () => {
    if (!validateForm()) return
    
    setLoading(true)
    try {
      // Parse all test case values
      const parsedExampleTestCases = exampleTestCases.map(tc => ({
        ...tc,
        inputs: tc.inputs.map(input => ({
          ...input,
          value: parseInputValue(input.value, input.data_type)
        })),
        output: {
          ...tc.output,
          value: parseInputValue(tc.output.value, tc.output.data_type)
        }
      }))
      
      const parsedHiddenTestCases = hiddenTestCases.map(tc => ({
        ...tc,
        inputs: tc.inputs.map(input => ({
          ...input,
          value: parseInputValue(input.value, input.data_type)
        })),
        output: {
          ...tc.output,
          value: parseInputValue(tc.output.value, tc.output.data_type)
        }
      }))
      
      // Build the question data
      const questionData = {
        title,
        description: title, // Use title as description
        category: category?.collegeType && category?.tier 
          ? `${category.collegeType}_${category.tier}` 
          : 'engineering_tier1',
        subcategory: null,
        difficulty_level: difficultyLevel,
        tags: null,
        language_restrictions: allowedLanguages,
        starter_code: starterCode.filter(sc => sc.code.trim()).map(sc => ({
          language: sc.language,
          code: sc.code
        })),
        question_data: {
          problem_statement: problemStatement,
          example_test_cases: parsedExampleTestCases,
          hidden_test_cases: parsedHiddenTestCases,
          function_signature: {
            function_name: functionName,
            parameters: parameters.filter(p => p.name.trim() && p.type.trim()),
            return_type: returnType
          },
          constraints: constraints.filter(c => c.trim()).length > 0
            ? { items: constraints.filter(c => c.trim()) }
            : null,
          hints: null,
          time_complexity_expected: timeComplexity || null,
          space_complexity_expected: spaceComplexity || null
        },
        // Add Judge0 Harness configuration
        judge0_harness_config: {
          input_format: judge0Config.input_format,
          output_format: judge0Config.output_format,
          function_name: judge0Config.function_name,
          templates: judge0Config.templates,
          custom_input_format: judge0Config.custom_input_format,
          custom_output_format: judge0Config.custom_output_format,
          parameter_count: parameters.filter(p => p.name.trim() && p.type.trim()).length
        }
      }
      
      // Submit to backend
      await codingQuestionsAPI.createCodingQuestion(questionData)
      
      toast.success('Coding question created successfully!')
      
      // Navigate back
      navigate(-1)
      
    } catch (error: any) {
      console.error('Error creating coding question:', error)
      toast.error(error.response?.data?.detail || 'Failed to create coding question')
    } finally {
      setLoading(false)
    }
  }
  
  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Invalid navigation state</p>
          <button onClick={() => navigate('/recruiter/question-bank')} className="btn btn-primary">
            Go to Question Bank
          </button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back
            </button>
          </div>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create Coding Question</h1>
            <p className="text-gray-600 mt-2">
              {category.collegeType === 'engineering' ? 'Engineering' : 'Management'} - 
              {category.tier === 'tier1' ? ' Tier 1' : ' Tier 2'}
            </p>
          </div>
        </div>

        {/* Form Sections */}
        <div className="space-y-4">
          
          {/* 1. Basic Information */}
          <CollapsibleSection
            title="Basic Information"
            isExpanded={expandedSections.basicInfo}
            onToggle={() => toggleSection('basicInfo')}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input w-full"
                  placeholder="e.g., Two Sum"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Problem Statement *
                </label>
                <textarea
                  value={problemStatement}
                  onChange={(e) => setProblemStatement(e.target.value)}
                  className="input w-full h-40"
                  placeholder="Detailed problem description..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Difficulty Level *
                </label>
                <select
                  value={difficultyLevel}
                  onChange={(e) => setDifficultyLevel(e.target.value as any)}
                  className="input w-full"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              
            </div>
          </CollapsibleSection>
          
          {/* 2. Function Signature */}
          <CollapsibleSection
            title="Function Signature"
            isExpanded={expandedSections.functionSignature}
            onToggle={() => toggleSection('functionSignature')}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Function Name *
                </label>
                <input
                  type="text"
                  value={functionName}
                  onChange={(e) => setFunctionName(e.target.value)}
                  className="input w-full font-mono"
                  placeholder="twoSum"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Parameters *
                  </label>
                  {parameters.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setParameters([{ name: '', type: '' }])
                        toast.success('Parameters cleared. Please add them one by one.')
                      }}
                      className="btn btn-sm btn-outline text-red-600 hover:bg-red-50"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Instructions */}
                <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="text-sm text-yellow-800">
                    <strong>📝 Instructions:</strong>
                    <ul className="mt-1 ml-4 list-disc text-xs">
                      <li>Click "Add Parameter" to create each parameter separately</li>
                      <li>For each parameter, enter the <strong>name only</strong> (e.g., "nums")</li>
                      <li>Enter the <strong>type only</strong> (e.g., "List[int]")</li>
                      <li><strong>Do NOT</strong> enter multiple parameters in one field</li>
                    </ul>
                    <div className="mt-2 p-2 bg-white rounded border">
                      <strong>✅ Example for function: topKFrequent(nums, k):</strong>
                      <div className="text-xs mt-1">
                        <div>Parameter 1: Name="nums", Type="List[int]"</div>
                        <div>Parameter 2: Name="k", Type="int"</div>
                        <div className="text-red-600">❌ Wrong: Name="self, nums, k", Type="int"</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {parameters.length === 0 ? (
                    <div className="text-sm text-gray-500 p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
                      No parameters added yet. Click "Add Parameter" below to get started.
                    </div>
                  ) : (
                    parameters.map((param, index) => (
                      <div key={index} className="flex gap-2 p-2 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-600 mb-1">Parameter Name</label>
                          <input
                            type="text"
                            value={param.name}
                            onChange={(e) => updateParameter(index, 'name', e.target.value)}
                            className="input w-full font-mono text-sm"
                            placeholder="e.g., nums (NOT: self, nums, k)"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-600 mb-1">Parameter Type</label>
                          <input
                            type="text"
                            value={param.type}
                            onChange={(e) => updateParameter(index, 'type', e.target.value)}
                            className="input w-full font-mono text-sm"
                            placeholder="e.g., List[int] (NOT: int, List[int], int)"
                          />
                        </div>
                        {parameters.length > 1 && (
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() => removeParameter(index)}
                              className="btn btn-outline text-red-600 hover:bg-red-50 text-sm"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  
                  {/* Add Parameter Button - Now below the parameter fields */}
                  <div className="flex justify-center pt-2">
                    <button
                      type="button"
                      onClick={addParameter}
                      className="btn btn-sm btn-outline flex items-center"
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Add Parameter
                    </button>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Return Type *
                </label>
                <input
                  type="text"
                  value={returnType}
                  onChange={(e) => setReturnType(e.target.value)}
                  className="input w-full font-mono"
                  placeholder="e.g., List[int]"
                />
              </div>
              
              {/* Function Preview */}
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                <code>
                  def {functionName || 'functionName'}(
                  {parameters.map(p => `${p.name}: ${p.type}`).join(', ')}
                  ) -&gt; {returnType || 'ReturnType'}:
                </code>
              </div>
              
              
              {/* Auto-generate starter code button */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Auto-generate starter code templates with function signature
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // Check if parameters are properly set up
                    const validParams = parameters.filter(p => p.name.trim() && p.type.trim())
                    if (validParams.length === 0) {
                      toast.error('Please add parameters first! Click the "+" button and fill in parameter name and type.')
                      return
                    }
                    
                    if (!returnType.trim()) {
                      toast.error('Please set the return type!')
                      return
                    }
                    
                    allowedLanguages.forEach(lang => {
                      if (isValidLanguage(lang)) {
                        updateStarterCodeForLanguage(lang)
                      }
                    })
                    toast.success('Starter code generated for all allowed languages!')
                  }}
                  className="btn btn-sm btn-outline"
                  disabled={!functionName.trim() || !returnType.trim()}
                >
                  Generate Starter Code
                </button>
              </div>
            </div>
          </CollapsibleSection>
          
          {/* 3. Example Test Cases */}
          <CollapsibleSection
            title="Example Test Cases (Visible to Candidates)"
            isExpanded={expandedSections.exampleTests}
            onToggle={() => toggleSection('exampleTests')}
            badge={exampleTestCases.length}
          >
            <div className="space-y-4">
              {exampleTestCases.map((testCase, tcIndex) => (
                <TestCaseCard
                  key={testCase.id}
                  title={`Example ${tcIndex + 1}`}
                  onRemove={() => removeExampleTestCase(testCase.id)}
                  canRemove={exampleTestCases.length > 1}
                >
                  {/* Inputs */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Inputs</label>
                      <button
                        type="button"
                        onClick={() => addExampleInput(testCase.id)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Input
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {testCase.inputs.map((input, inputIndex) => (
                        <div key={inputIndex} className="grid grid-cols-12 gap-2">
                          <input
                            type="text"
                            value={input.name}
                            onChange={(e) => updateExampleInput(testCase.id, inputIndex, 'name', e.target.value)}
                            className="input col-span-3 text-sm"
                            placeholder="Name"
                          />
                          <select
                            value={input.data_type}
                            onChange={(e) => updateExampleInput(testCase.id, inputIndex, 'data_type', e.target.value)}
                            className="input col-span-3 text-sm"
                          >
                            {DATA_TYPES.map(dt => (
                              <option key={dt.value} value={dt.value}>{dt.label}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={input.value}
                            onChange={(e) => updateExampleInput(testCase.id, inputIndex, 'value', e.target.value)}
                            className="input col-span-5 text-sm font-mono"
                            placeholder="Value (e.g., [1,2,3])"
                          />
                          {testCase.inputs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeExampleInput(testCase.id, inputIndex)}
                              className="btn btn-sm col-span-1 text-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Output */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expected Output</label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={testCase.output.data_type}
                        onChange={(e) => updateExampleTestCase(testCase.id, 'output', {
                          ...testCase.output,
                          data_type: e.target.value
                        })}
                        className="input text-sm"
                      >
                        {DATA_TYPES.map(dt => (
                          <option key={dt.value} value={dt.value}>{dt.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={testCase.output.value}
                        onChange={(e) => updateExampleTestCase(testCase.id, 'output', {
                          ...testCase.output,
                          value: e.target.value
                        })}
                        className="input text-sm font-mono"
                        placeholder="Expected value"
                      />
                    </div>
                  </div>
                  
                  {/* Explanation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Explanation</label>
                    <textarea
                      value={testCase.explanation}
                      onChange={(e) => updateExampleTestCase(testCase.id, 'explanation', e.target.value)}
                      className="input w-full h-20 text-sm"
                      placeholder="Explain why this output is expected..."
                    />
                  </div>
                </TestCaseCard>
              ))}
              
              <button
                type="button"
                onClick={addExampleTestCase}
                className="btn btn-outline w-full"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Example Test Case
              </button>
            </div>
          </CollapsibleSection>
          
          {/* 4. Hidden Test Cases */}
          <CollapsibleSection
            title="Hidden Test Cases"
            isExpanded={expandedSections.hiddenTests}
            onToggle={() => toggleSection('hiddenTests')}
            badge={hiddenTestCases.length}
          >
            <div className="space-y-4">
              {hiddenTestCases.map((testCase, tcIndex) => (
                <TestCaseCard
                  key={testCase.id}
                  title={`Hidden Test ${tcIndex + 1}`}
                  onRemove={() => removeHiddenTestCase(testCase.id)}
                  canRemove={hiddenTestCases.length > 1}
                  variant="hidden"
                >
                  {/* Inputs */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Inputs</label>
                      <button
                        type="button"
                        onClick={() => addHiddenInput(testCase.id)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Input
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {testCase.inputs.map((input, inputIndex) => (
                        <div key={inputIndex} className="grid grid-cols-12 gap-2">
                          <input
                            type="text"
                            value={input.name}
                            onChange={(e) => updateHiddenInput(testCase.id, inputIndex, 'name', e.target.value)}
                            className="input col-span-3 text-sm"
                            placeholder="Name"
                          />
                          <select
                            value={input.data_type}
                            onChange={(e) => updateHiddenInput(testCase.id, inputIndex, 'data_type', e.target.value)}
                            className="input col-span-3 text-sm"
                          >
                            {DATA_TYPES.map(dt => (
                              <option key={dt.value} value={dt.value}>{dt.label}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={input.value}
                            onChange={(e) => updateHiddenInput(testCase.id, inputIndex, 'value', e.target.value)}
                            className="input col-span-5 text-sm font-mono"
                            placeholder="Value"
                          />
                          {testCase.inputs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeHiddenInput(testCase.id, inputIndex)}
                              className="btn btn-sm col-span-1 text-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Output */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expected Output</label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={testCase.output.data_type}
                        onChange={(e) => updateHiddenTestCase(testCase.id, 'output', {
                          ...testCase.output,
                          data_type: e.target.value
                        })}
                        className="input text-sm"
                      >
                        {DATA_TYPES.map(dt => (
                          <option key={dt.value} value={dt.value}>{dt.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={testCase.output.value}
                        onChange={(e) => updateHiddenTestCase(testCase.id, 'output', {
                          ...testCase.output,
                          value: e.target.value
                        })}
                        className="input text-sm font-mono"
                        placeholder="Expected value"
                      />
                    </div>
                  </div>
                  
                  {/* Weight */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weight (Points)
                    </label>
                    <input
                      type="number"
                      value={testCase.weight}
                      onChange={(e) => updateHiddenTestCase(testCase.id, 'weight', parseFloat(e.target.value))}
                      className="input w-32 text-sm"
                      min="0"
                      max="10"
                      step="0.5"
                    />
                  </div>
                </TestCaseCard>
              ))}
              
              <button
                type="button"
                onClick={addHiddenTestCase}
                className="btn btn-outline w-full"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Hidden Test Case
              </button>
            </div>
          </CollapsibleSection>
          
          {/* 5. Constraints (Optional) */}
          <CollapsibleSection
            title="Constraints (Optional)"
            isExpanded={expandedSections.constraints}
            onToggle={() => toggleSection('constraints')}
          >
            <div className="space-y-2">
              {constraints.map((constraint, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={constraint}
                    onChange={(e) => updateConstraint(index, e.target.value)}
                    className="input flex-1 text-sm font-mono"
                    placeholder="e.g., 2 <= nums.length <= 10^4"
                  />
                  <button
                    type="button"
                    onClick={() => removeConstraint(index)}
                    className="btn btn-outline text-red-600"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addConstraint}
                className="btn btn-sm btn-outline"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Add Constraint
              </button>
            </div>
          </CollapsibleSection>
          
          {/* 6. Starter Code Templates */}
          <CollapsibleSection
            title="Starter Code Templates"
            isExpanded={expandedSections.starterCode}
            onToggle={() => toggleSection('starterCode')}
          >
            <div className="space-y-4">
              {starterCode.map((sc, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <select
                      value={sc.language}
                      onChange={(e) => updateStarterCode(index, 'language', e.target.value)}
                      className="input w-40 text-sm"
                    >
                      {PROGRAMMING_LANGUAGES.map(lang => (
                        <option key={lang.value} value={lang.value}>{lang.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeStarterCode(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea
                    value={sc.code}
                    onChange={(e) => updateStarterCode(index, 'code', e.target.value)}
                    className="input w-full h-32 font-mono text-sm"
                    placeholder={isValidLanguage(sc.language) ? generateStarterCode(sc.language) : `# Starter code for ${sc.language}\ndef ${functionName}(...):\n    pass`}
                  />
                  {!sc.code.trim() && functionName.trim() && returnType.trim() && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                      💡 Click "Generate Starter Code" above to auto-fill this template
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addStarterCode}
                className="btn btn-outline w-full"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Starter Code for Another Language
              </button>
            </div>
          </CollapsibleSection>
          
          {/* 7. Judge0 Harness Configuration */}
          <CollapsibleSection
            title="Judge0 Harness Configuration (Auto-Generated)"
            isExpanded={expandedSections.judge0Harness}
            onToggle={() => toggleSection('judge0Harness')}
          >
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">🤖 Auto-Generated Configuration</p>
                    <p>This configuration is automatically generated based on your function signature. It enables your coding question to work with the Judge0 Harness system for code execution.</p>
                    <div className="mt-2 p-2 bg-white rounded border text-xs">
                      <strong>💡 Multiple Parameters Support:</strong>
                      <ul className="mt-1 ml-4 list-disc">
                        <li>For <code>string + integer</code> → "String + Integer" format</li>
                        <li>For <code>array + integer</code> → "Array + Integer" format</li>
                        <li>For <code>string + array</code> → "String + Array" format</li>
                        <li>For 3+ parameters → "Multiple Parameters (Custom)" format</li>
                      </ul>
                    </div>
                    <div className="mt-2 p-2 bg-white rounded border text-xs">
                      <strong>🎯 Complex Output Support:</strong>
                      <ul className="mt-1 ml-4 list-disc">
                        <li>For <code>List[string]</code> → "Array of Strings" format</li>
                        <li>For <code>List[int]</code> → "Array of Integers" format</li>
                        <li>For <code>Object with arrays</code> → "Object with Array" format</li>
                        <li>For <code>Tuple</code> → "Tuple (Multiple Values)" format</li>
                        <li>For <code>Boolean</code> → "Boolean" format</li>
                      </ul>
                    </div>
                    <div className="mt-2 p-2 bg-white rounded border text-xs">
                      <strong>🔧 Custom Format Support:</strong>
                      <ul className="mt-1 ml-4 list-disc">
                        <li>When auto-detection fails → Use "Custom Input/Output Format"</li>
                        <li>For unique parameter combinations → Specify custom format</li>
                        <li>For complex nested structures → Describe the exact format</li>
                        <li>For edge cases → Provide detailed format specification</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Current Function Signature Display */}
              {(parameters.length > 0 || returnType) && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-2">Current Function Signature:</div>
                  
                  {/* Parameters */}
                  {parameters.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-gray-600 mb-1">Parameters:</div>
                      <div className="flex flex-wrap gap-2">
                        {parameters.map((param, index) => (
                          <span key={index} className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">
                            {param.name}: {param.type}
                          </span>
                        ))}
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        → Input format: <span className="font-medium text-blue-600">{judge0Config.input_format.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Return Type */}
                  {returnType && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Return Type:</div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">
                          {returnType}
                        </span>
                        <div className="text-xs text-gray-600">
                          → Output format: <span className="font-medium text-green-600">{judge0Config.output_format.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Input Format
                  </label>
                  <select
                    value={judge0Config.input_format}
                    onChange={(e) => setJudge0Config(prev => ({
                      ...prev,
                      input_format: e.target.value as Judge0HarnessConfig['input_format']
                    }))}
                    className="input w-full"
                  >
                    {INPUT_FORMATS.map(format => (
                      <option key={format.value} value={format.value}>{format.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {judge0Config.input_format === 'multiple_parameters' 
                      ? 'Custom format for 3+ parameters - will be handled dynamically'
                      : judge0Config.input_format === 'custom_input'
                      ? 'Specify your custom input format below'
                      : 'Automatically selected based on your function parameters'
                    }
                  </p>
                  
                  {/* Custom Input Format Field */}
                  {judge0Config.input_format === 'custom_input' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Input Format Specification
                      </label>
                      <textarea
                        value={judge0Config.custom_input_format || ''}
                        onChange={(e) => setJudge0Config(prev => ({
                          ...prev,
                          custom_input_format: e.target.value
                        }))}
                        className="input w-full h-20 text-sm font-mono"
                        placeholder="Describe your custom input format...&#10;Example: 'Two space-separated integers followed by an array of strings'&#10;Example: 'JSON object with keys: data (array), options (object), count (integer)'"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Describe how your input parameters should be formatted for Judge0 execution
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Output Format
                  </label>
                  <select
                    value={judge0Config.output_format}
                    onChange={(e) => setJudge0Config(prev => ({
                      ...prev,
                      output_format: e.target.value as Judge0HarnessConfig['output_format']
                    }))}
                    className="input w-full"
                  >
                    {OUTPUT_FORMATS.map(format => (
                      <option key={format.value} value={format.value}>{format.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {judge0Config.output_format === 'complex_output' 
                      ? 'Custom format for complex return types - will be handled dynamically'
                      : judge0Config.output_format === 'custom_output'
                      ? 'Specify your custom output format below'
                      : 'Automatically selected based on your return type'
                    }
                  </p>
                  
                  {/* Custom Output Format Field */}
                  {judge0Config.output_format === 'custom_output' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Output Format Specification
                      </label>
                      <textarea
                        value={judge0Config.custom_output_format || ''}
                        onChange={(e) => setJudge0Config(prev => ({
                          ...prev,
                          custom_output_format: e.target.value
                        }))}
                        className="input w-full h-20 text-sm font-mono"
                        placeholder="Describe your custom output format...&#10;Example: 'Array of objects with keys: id (integer), name (string), active (boolean)'&#10;Example: 'Tuple of (string, integer, array) in JSON format'"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Describe how your output should be formatted and compared for Judge0 execution
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Function Name
                </label>
                <input
                  type="text"
                  value={judge0Config.function_name}
                  onChange={(e) => setJudge0Config(prev => ({
                    ...prev,
                    function_name: e.target.value
                  }))}
                  className="input w-full font-mono"
                  placeholder="Function name for Judge0 Harness"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Files
                </label>
                <div className="space-y-2">
                  {Object.entries(judge0Config.templates).map(([lang, template]) => (
                    <div key={lang} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium text-gray-700 capitalize">{lang}</span>
                      <span className="text-sm text-gray-600 font-mono">{template}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  These template files are automatically configured for optimal Judge0 Harness integration.
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Regenerate configuration based on current function signature
                </span>
                <button
                  type="button"
                  onClick={generateJudge0Config}
                  className="btn btn-sm btn-outline"
                  disabled={!functionName.trim() || !returnType.trim()}
                >
                  Regenerate Config
                </button>
              </div>
            </div>
          </CollapsibleSection>
          
          {/* 8. Advanced Settings */}
          <CollapsibleSection
            title="Advanced Settings (Optional)"
            isExpanded={expandedSections.advanced}
            onToggle={() => toggleSection('advanced')}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Time Complexity
                </label>
                <input
                  type="text"
                  value={timeComplexity}
                  onChange={(e) => setTimeComplexity(e.target.value)}
                  className="input w-full font-mono"
                  placeholder="e.g., O(n)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Space Complexity
                </label>
                <input
                  type="text"
                  value={spaceComplexity}
                  onChange={(e) => setSpaceComplexity(e.target.value)}
                  className="input w-full font-mono"
                  placeholder="e.g., O(1)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed Programming Languages
                </label>
                <div className="space-y-2">
                  {PROGRAMMING_LANGUAGES.map(lang => (
                    <label key={lang.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={allowedLanguages.includes(lang.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAllowedLanguages([...allowedLanguages, lang.value])
                          } else {
                            setAllowedLanguages(allowedLanguages.filter(l => l !== lang.value))
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">{lang.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </CollapsibleSection>
          
        </div>
        
        {/* Fixed bottom action bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-outline"
                disabled={loading}
              >
                Cancel
              </button>
              
              <button
                onClick={handleSubmit}
                className="btn btn-primary px-8"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Coding Question'}
              </button>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  )
}

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string
  isExpanded: boolean
  onToggle: () => void
  children: React.ReactNode
  badge?: number
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  isExpanded,
  onToggle,
  children,
  badge
}) => {
  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {badge !== undefined && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-100">
          <div className="pt-4">
            {children}
          </div>
        </div>
      )}
    </div>
  )
}

// Test Case Card Component
interface TestCaseCardProps {
  title: string
  onRemove: () => void
  canRemove: boolean
  children: React.ReactNode
  variant?: 'example' | 'hidden'
}

const TestCaseCard: React.FC<TestCaseCardProps> = ({
  title,
  onRemove,
  canRemove,
  children,
  variant = 'example'
}) => {
  const bgColor = variant === 'hidden' ? 'bg-purple-50' : 'bg-gray-50'
  const borderColor = variant === 'hidden' ? 'border-purple-200' : 'border-gray-200'
  
  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">{title}</h4>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-red-600 hover:text-red-800"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

export default CreateCodingQuestionPage

